import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import imageCompression from 'browser-image-compression';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import {
  Box,
  Button,
  Card,
  Column,
  FileDropzone,
  IconButton,
  Row,
  Typography,
} from '@tempmee/react';
import {
  OutlineChevronLeftIcon,
  OutlineChevronRightIcon,
  OutlinePlusIcon,
  OutlineRedoIcon,
  OutlineTrashIcon,
} from '@tempmee/react-icons';
import { useRotatedImageUrl } from './hooks/useRotatedImageUrl';
import styles from './StateCredentialFileV2.module.scss';

const fileKey = (file) => file.file_id || file.url;

// Room around the zoomed image, so it never sits under the controls at the bottom.
const ZOOM_MARGIN = 80;

/** Compress images to ≤ 1 MB; anything that fails to compress (e.g. a PDF) is kept as is. */
const toLocalFile = async (file, index) => {
  let uploaded = file;
  try {
    uploaded = await imageCompression(file, { maxSizeMB: 1 });
  } catch {
    // compression failed, use original
  }
  return {
    // Marked local: uploaded by the parent's Save, not here.
    _local: true,
    _file: uploaded,
    url: URL.createObjectURL(uploaded),
    file_id: `local-${Date.now()}-${index}`,
  };
};

const Thumbnail = ({ file, index, total, selected, editMode, onSelect, onDelete }) => {
  const select = () => onSelect(index);
  return (
    <Box className="relative shrink-0">
      <Card
        interactive
        role="button"
        tabIndex={0}
        aria-label={`Show file ${index + 1} of ${total}`}
        aria-pressed={selected}
        onClick={select}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            select();
          }
        }}
        className={`overflow-hidden p-0 w-24 h-16 border-2 ${
          selected ? 'border-primary' : 'border-divider'
        }`}
      >
        <img src={file.url} alt="" className="w-full h-full object-cover" />
      </Card>
      {editMode && (
        <span className={styles.edgeActions}>
          <IconButton
            Icon={<OutlineTrashIcon size={14} />}
            aria-label={`Delete file ${index + 1} of ${total}`}
            appearance="filled"
            color="error"
            size="small"
            onClick={() => onDelete(file)}
          />
        </span>
      )}
    </Box>
  );
};

// The zoom view's controls. Zoom takes a component type for its content, so the widget's
// state reaches it through context: an inline component would remount on every render.
const ZoomControlsContext = createContext(null);

/**
 * The zoomed (full-screen) view: which file this is above the image, Previous / Rotate / Next
 * below it. After a Rotate the image is remounted with a short spin into its new position.
 */
const ZoomedFile = ({ img, buttonUnzoom }) => {
  const { caption, total, spin, onSpinEnd, onRotate, onStep } = useContext(ZoomControlsContext);
  return (
    <>
      <Typography variant="subtitle1" as="p" className={styles.zoomCaption}>
        {caption}
      </Typography>
      {img &&
        cloneElement(img, {
          key: spin.count,
          // Dropped when done: a closed zoom view is display: none, and showing it again
          // would replay a class-driven animation.
          className: spin.active ? styles.spin : undefined,
          onAnimationEnd: onSpinEnd,
        })}
      {buttonUnzoom}
      <Row gap={2} align="center" className={styles.zoomControls}>
        {total > 1 && (
          <IconButton
            Icon={<OutlineChevronLeftIcon size={18} />}
            aria-label="Previous file"
            appearance="filled"
            color="secondary"
            onClick={() => onStep(-1)}
          />
        )}
        <Button
          variant="outlined"
          color="secondary"
          // The DS rotate arrow turns counter-clockwise; mirrored, it matches the clockwise turn.
          startIcon={<OutlineRedoIcon size={18} className={styles.clockwise} />}
          onClick={onRotate}
        >
          Rotate
        </Button>
        {total > 1 && (
          <IconButton
            Icon={<OutlineChevronRightIcon size={18} />}
            aria-label="Next file"
            appearance="filled"
            color="secondary"
            onClick={() => onStep(1)}
          />
        )}
      </Row>
    </>
  );
};

ZoomedFile.propTypes = {
  img: PropTypes.node,
  buttonUnzoom: PropTypes.node.isRequired,
};

ZoomedFile.defaultProps = {
  img: null,
};

Thumbnail.propTypes = {
  file: PropTypes.shape({ url: PropTypes.string }).isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

/**
 * V2 credential file upload component.
 *
 * Controlled: it renders `files` as given and reports every add or delete through
 * `onFilesChange`, so the parent's draft is the only copy (a Cancel that restores the
 * saved list shows the saved list). Added files stay local; the parent uploads them on Save.
 */
export const StateCredentialFileV2 = ({ title, editMode, files, onFilesChange }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const pickerRef = useRef(null);
  // blob: URLs this widget created. Only these are revoked: files the store prefetched carry
  // blob: URLs it owns and still shows after this widget unmounts.
  const createdUrls = useRef(new Set());

  // Release a created URL once its file leaves the list (deleted here, or dropped by Cancel).
  useEffect(() => {
    const live = new Set(files.map((f) => f.url));
    createdUrls.current.forEach((url) => {
      if (!live.has(url)) {
        URL.revokeObjectURL(url);
        createdUrls.current.delete(url);
      }
    });
  }, [files]);

  useEffect(() => {
    const created = createdUrls.current;
    return () => created.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  // The list can shrink from outside (e.g. Cancel), so keep the selection in range.
  const currentIndex = Math.min(selectedIndex, Math.max(files.length - 1, 0));
  const current = files[currentIndex] || null;

  // A display-only rotation per file, for sideways uploads; set in the zoom view, never saved.
  const [quarterTurns, setQuarterTurns] = useState({});
  const currentTurns = current ? quarterTurns[fileKey(current)] || 0 : 0;
  const currentSrc = useRotatedImageUrl(current?.url, currentTurns);
  const caption = `${title} ${currentIndex + 1} of ${files.length}`;

  // Spin the zoomed image once its rotated copy is on screen (it is drawn asynchronously), and
  // only after a Rotate: stepping to a file that is already rotated just shows it.
  const [spin, setSpin] = useState({ count: 0, active: false });
  const spinPending = useRef(false);
  useEffect(() => {
    if (spinPending.current) {
      spinPending.current = false;
      setSpin(({ count }) => ({ count: count + 1, active: true }));
    }
  }, [currentSrc]);

  const total = files.length;
  const currentKey = current ? fileKey(current) : null;
  // Read by the zoom view through context; stable unless what it shows changes.
  const zoomControls = useMemo(
    () => ({
      caption,
      total,
      spin,
      onSpinEnd: () => setSpin(({ count }) => ({ count, active: false })),
      onRotate: () => {
        spinPending.current = true;
        setQuarterTurns((turns) => ({
          ...turns,
          [currentKey]: ((turns[currentKey] || 0) + 1) % 4,
        }));
      },
      onStep: (delta) => {
        spinPending.current = false;
        setSelectedIndex((currentIndex + delta + total) % total);
      },
    }),
    [caption, total, spin, currentKey, currentIndex],
  );
  const { onStep: step } = zoomControls;

  const handleAddFiles = async (selected) => {
    const added = await Promise.all(selected.map(toLocalFile));
    added.forEach((f) => createdUrls.current.add(f.url));
    const newFiles = [...files, ...added];
    onFilesChange?.(newFiles);
    setSelectedIndex(newFiles.length - 1);
  };

  const handleDelete = (file) => {
    const newFiles = files.filter((f) => fileKey(f) !== fileKey(file));
    onFilesChange?.(newFiles);
    if (currentIndex >= newFiles.length) {
      setSelectedIndex(Math.max(0, newFiles.length - 1));
    }
  };

  // The + button opens this dropzone's own picker, so there is no hand-rolled file input.
  const openPicker = () => pickerRef.current?.querySelector('input[type="file"]')?.click();

  if (files.length === 0) {
    return editMode ? (
      <Box width="100%">
        <FileDropzone
          browseLabel="Browse"
          hint="Images are compressed to 1 MB. Files upload when you save."
          onSelectFiles={handleAddFiles}
        />
      </Box>
    ) : null;
  }

  return (
    <Column
      align="center"
      gap={editMode ? 8 : 4}
      p={6}
      width="100%"
      className="rounded border border-dashed border-neutral-200"
    >
      {/* The preview with its controls on the edges, as in the original widget. */}
      <Box className="relative max-w-full">
        {/* 3:2 preview, sized to fit a MacBook-width card; click it to zoom. Stepping or
            rotating while zoomed swaps the image's src, and the zoom view follows. */}
        <Box width="240px" height="160px" className="overflow-hidden rounded bg-neutral-150">
          {current && (
            <ZoomControlsContext.Provider value={zoomControls}>
              <Zoom
                a11yNameButtonZoom="View full size"
                a11yNameButtonUnzoom="Close full size"
                zoomMargin={ZOOM_MARGIN}
                ZoomContent={ZoomedFile}
              >
                <img src={currentSrc} alt={caption} className={styles.preview} />
              </Zoom>
            </ZoomControlsContext.Provider>
          )}
        </Box>

        {files.length > 1 && (
          <>
            <span className={styles.prev}>
              <IconButton
                Icon={<OutlineChevronLeftIcon size={16} />}
                aria-label="Previous file"
                appearance="filled"
                color="secondary"
                size="small"
                onClick={() => step(-1)}
              />
            </span>
            <span className={styles.next}>
              <IconButton
                Icon={<OutlineChevronRightIcon size={16} />}
                aria-label="Next file"
                appearance="filled"
                color="secondary"
                size="small"
                onClick={() => step(1)}
              />
            </span>
          </>
        )}

        {editMode && (
          <span className={styles.edgeActions}>
            <IconButton
              Icon={<OutlinePlusIcon size={16} />}
              aria-label="Add file"
              appearance="filled"
              color="primary"
              size="small"
              onClick={openPicker}
            />
            <IconButton
              Icon={<OutlineTrashIcon size={16} />}
              aria-label="Delete file"
              appearance="filled"
              color="error"
              size="small"
              onClick={() => current && handleDelete(current)}
            />
          </span>
        )}
      </Box>

      {files.length > 1 && (
        // The row scrolls sideways, which also clips vertically: leave room below for the
        // delete buttons that hang off each thumbnail's bottom edge.
        <Row gap={2} className="overflow-x-auto max-w-full" pt={1} pb={editMode ? 5 : 1}>
          {files.map((file, index) => (
            <Thumbnail
              key={fileKey(file)}
              file={file}
              index={index}
              total={files.length}
              selected={index === currentIndex}
              editMode={editMode}
              onSelect={setSelectedIndex}
              onDelete={handleDelete}
            />
          ))}
        </Row>
      )}

      {/* Not rendered (display: none, so not focusable): only supplies the + button's picker. */}
      {editMode && (
        <Box className="hidden">
          <FileDropzone ref={pickerRef} onSelectFiles={handleAddFiles} />
        </Box>
      )}
    </Column>
  );
};

StateCredentialFileV2.propTypes = {
  /** What the files are, e.g. the credential's name; captions the zoom view ("<title> 1 of 3"). */
  title: PropTypes.string,
  editMode: PropTypes.bool,
  files: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string, file_id: PropTypes.string })),
  /** Receives the full new file list after every add or delete. */
  onFilesChange: PropTypes.func,
};

StateCredentialFileV2.defaultProps = {
  title: 'File',
  editMode: true,
  files: [],
  onFilesChange: undefined,
};
